import type {
  FacilitiesFilterParams,
  CreateFacilityDto,
  PaginatedFacilitiesResponseDto,
  UpdateFacilityDto,
} from '@peritasai/types';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { FacilityEntity } from './facility.entity';
import { facilitiesRepository } from './repository';

const FACILITY_SEARCH_LIMIT = 10;

export const facilitiesKeys = {
  all: ['facilities'] as const,
  lists: () => [...facilitiesKeys.all, 'list'] as const,
  list: (filters: FacilitiesFilterParams) =>
    [...facilitiesKeys.lists(), { filters }] as const,
  searches: () => [...facilitiesKeys.all, 'search'] as const,
  search: (query: string, page: number) =>
    [...facilitiesKeys.searches(), { query, page }] as const,
} as const;

export function useFacilities(
  filters: FacilitiesFilterParams = {}
): UseQueryResult<PaginatedFacilitiesResponseDto & { facilities: FacilityEntity[] }> {
  return useQuery({
    queryKey: facilitiesKeys.list(filters),
    queryFn: () => facilitiesRepository.getFacilitiesPaginated(filters),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

export function useFacilitySearch(
  searchQuery: string | null,
  page = 1,
): UseQueryResult<PaginatedFacilitiesResponseDto & { facilities: FacilityEntity[] }> {
  return useQuery({
    queryKey: facilitiesKeys.search(searchQuery ?? '', page),
    queryFn: () =>
      facilitiesRepository.getFacilitiesPaginated({
        search: searchQuery ?? undefined,
        limit: FACILITY_SEARCH_LIMIT,
        page: page - 1,
      }),
    enabled: searchQuery !== null,
    staleTime: 60 * 1000,
  });
}

export function useCreateFacility(): UseMutationResult<FacilityEntity, Error, CreateFacilityDto> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateFacilityDto) => facilitiesRepository.createFacility(dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: facilitiesKeys.lists() });
    },
  });
}

export function useUpdateFacility(): UseMutationResult<
  FacilityEntity,
  Error,
  { id: string; dto: UpdateFacilityDto }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateFacilityDto }) =>
      facilitiesRepository.updateFacility(id, dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: facilitiesKeys.lists() });
    },
  });
}
